import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { getCaptchaErrorMessage, getCaptchaSiteKey, useAmsSelector, useCaptcha, useTranslation } from "@awe-web/shared/lib";
import {
  getUserEmail,
  newValidate,
  newGetInitialValues,
  newGetFieldLabel,
  newIsFieldRequired,
  newHasField
} from "@awe-web/shared/lib";
import { useInitialize } from "../../../util/react.js";
import { TypeArea } from "../../../components/layout.js";
import { Row, Col } from "../../../components/grid.js";
import { ScreenSection, ScreenHeading, ScreenSectionHeading, ScreenLoading } from "../../../components/screen.js";
import {
  FormikLabeledField,
  Button,
  FormikPasswordInput,
  FormikTextInput,
  ErrorMessage
} from "../../../components/form.js";
import { ConsentFieldset } from "../../../user/components/form.js";
import { getCompanyAddress, getSalesTaxId, getContactPerson, getIsProfileReadOnly } from "../../../user/store.js";
import { initialize, submitPassword, submitConsent, submitTaxData } from "./actions.js";

import "./profile.scss";
import HCaptcha from '@hcaptcha/react-hcaptcha';

const PROFILE = "ALG0000120";
const ADDRESS_HEADING = "ALG0000121";
const TAX_DATA_HEADING = "ALG0000267";
const CONTACT_HEADING = "ALG0000122";
const SALES_TAX_ID = "ALG0000123";
const CONTACT_E_MAIL_ADDRESS = "ALG0000124";
const CONTACT_PHONE_NUMBER = "ALG0000125";
const LOGIN_CREDENTIALS_HEADING = "ALG0000126";
const E_MAIL = "ALG0000127";
const LEGAL_HEADING = "ALG0000129";
const SUBMIT = "ALG0000130";

function Line({ value }) {
  return value ? <div>{value}</div> : null;
}

function InternalProfileForm({ onSubmitTaxData, onSubmitConsent, onSubmitPassword, onCaptchaChange }) {
  const { translate } = useTranslation();

  const isReadOnly = useSelector((state) => getIsProfileReadOnly(state));
  const companyAddressState = useSelector((state) => getCompanyAddress(state));
  const salesTaxId = useSelector((state) => getSalesTaxId(state));
  const contactPersonDetailsState = useSelector((state) => getContactPerson(state));
  const contactPersonDetails = useMemo(() => {
    return contactPersonDetailsState ? contactPersonDetailsState.toJS() : {};
  },
    [contactPersonDetailsState]
  );

  const companyAddress = useMemo(() => {
    return companyAddressState ? companyAddressState.toJS() : {};
  },
    [companyAddressState]
  );
  const userEmail = useSelector((state) => getUserEmail(state));

  const validateTaxData = useAmsSelector((state) => newValidate("changeBusinessCustomer")(state));
  const isTaxDataFieldRequired = useAmsSelector((state) => newIsFieldRequired("changeBusinessCustomer")(state));
  const getTaxDataFieldLabel = useAmsSelector((state) => newGetFieldLabel("changeBusinessCustomer")(state));
  const hasTaxDataField = useAmsSelector((state) => newHasField("changeBusinessCustomer")(state));

  const validateConsent = useAmsSelector((state) => newValidate("BMWChangeCommunicationConfirmation")(state));
  const getConsentFieldLabel = useAmsSelector((state) => newGetFieldLabel("BMWChangeCommunicationConfirmation")(state));

  const validatePassword = useAmsSelector((state) => newValidate("changePassword")(state));
  const isPasswordFieldRequired = useAmsSelector((state) => newIsFieldRequired("changePassword")(state));
  const getPasswordFieldLabel = useAmsSelector((state) => newGetFieldLabel("changePassword")(state));

  const initialTaxDataValues = useAmsSelector((state) => {
    const taxFormInitializer = newGetInitialValues("changeBusinessCustomer");
    const taxInialValuesFactory = taxFormInitializer(state);
    return taxInialValuesFactory({
      "configuration": "changeBusinessCustomer",
      ...(hasTaxDataField("Customer:taxident") ? { "Customer:taxident": "" } : {}),
      ...(hasTaxDataField("Customer:itoident") ? { "Customer:itoident": "" } : {})
    });
  });

  const initialConsentValues = useAmsSelector((state) => {
    const consentFormInitializer = newGetInitialValues("BMWChangeCommunicationConfirmation");
    const consentInialValuesFactory = consentFormInitializer(state);
    return consentInialValuesFactory({
      "configuration": "BMWChangeCommunicationConfirmation",
      [ConsentFieldset.MAIL]: false,
      [ConsentFieldset.EMAIL]: false,
      [ConsentFieldset.PHONE]: false
    });
  });

  const initialPasswordValues = useAmsSelector((state) => {
    const passwordFormInitializer = newGetInitialValues("changePassword");
    const passwordInialValuesFactory = passwordFormInitializer(state);
    return passwordInialValuesFactory({
      "Customer:currentPassword": "",
      "Customer:password": ""
    });
  });

  const captchaPublicSiteKey = useSelector((state) => getCaptchaSiteKey(state));
  const { captchaRef, handleCaptcha, currentLanguage } = useCaptcha();
  const captchaErrorMessage = useSelector((state) => getCaptchaErrorMessage(state));

  const handleCaptchaChange = useCallback((token) => {
    onCaptchaChange(token);
    handleCaptcha(token);
  }, [handleCaptcha, onCaptchaChange]);

  return (
    <ScreenSection dark className="b2b-profile">
      <TypeArea>
        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <ScreenHeading>{translate(PROFILE)}</ScreenHeading>
          </Col>
        </Row>

        <Row>
          <Col tablet={{ cols: 4 }} desktop={{ cols: 3, offset: 2 }}>
            <ScreenSectionHeading light>
              {translate(ADDRESS_HEADING)}
            </ScreenSectionHeading>

            <div className="b2b-profile__address">
              <div className="b2b-profile__company-name">
                {companyAddress.companyName}
              </div>
              <Line value={companyAddress.street} />
              <Line value={companyAddress.building} />
              <Line value={`${companyAddress.zipCode} ${companyAddress.city}`} />
              <Line value={companyAddress.country} />
            </div>

            <div className="b2b-profile__sales-tax">
              <div className="b2b-profile__sales-tax-heading">{translate(SALES_TAX_ID)}</div>
              <div>{salesTaxId}</div>
            </div>

            <hr className="b2b-profile__separator b2b-profile__separator--mobile" />
          </Col>

          <Col tablet={{ cols: 4 }} desktop={{ cols: 3 }}>
            {contactPersonDetails && (
              <>
                <ScreenSectionHeading light>
                  {translate(CONTACT_HEADING)}
                </ScreenSectionHeading>

                <div className="b2b-profile__contact-person-name">
                  {contactPersonDetails.fullName}
                </div>

                <div className="b2b-profile__contact-detail b2b-profile__contact-detail--gap">
                  <div className="b2b-profile__contact-person-label">
                    {translate(CONTACT_E_MAIL_ADDRESS)}
                  </div>
                  <div>{contactPersonDetails.email}</div>
                </div>

                <div className="b2b-profile__contact-detail">
                  <div className="b2b-profile__contact-person-label">
                    {translate(CONTACT_PHONE_NUMBER)}
                  </div>
                  <div>{contactPersonDetails.phone}</div>
                </div>
              </>
            )}
          </Col>
        </Row>

        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <hr className="b2b-profile__separator" />
          </Col>
        </Row>

        {hasTaxDataField("Customer:taxident") && (
          <>
            <Row>
              <Col tablet={{ cols: 4 }} desktop={{ cols: 3, offset: 2 }}>
                <ScreenSectionHeading light>
                  {translate(TAX_DATA_HEADING)}
                </ScreenSectionHeading>
              </Col>
            </Row>

            <Formik
              initialValues={initialTaxDataValues}
              validate={validateTaxData}
              onSubmit={onSubmitTaxData}
            >
              {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit}>
                  <Row className="b2b-profile__tax-data-fields">
                    <Col
                      tablet={{ cols: 4 }}
                      desktop={{ cols: 4, offset: 2 }}
                    >
                      <FormikLabeledField
                        className="b2b-profile__form-row"
                        name="Customer:taxident"
                        as={FormikTextInput}
                        isFieldRequired={isTaxDataFieldRequired}
                        getFieldLabel={getTaxDataFieldLabel}
                        disabled={isReadOnly}
                      />
                    </Col>
                    <Col
                      tablet={{ cols: 4 }}
                      desktop={{ cols: 4 }}
                    >
                      <FormikLabeledField
                        className="b2b-profile__form-row"
                        name="Customer:itoident"
                        as={FormikTextInput}
                        isFieldRequired={isTaxDataFieldRequired}
                        getFieldLabel={getTaxDataFieldLabel}
                        disabled={isReadOnly}
                      />
                    </Col>
                  </Row>

                  {!isReadOnly && captchaErrorMessage && <Row>
                    <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
                      <ErrorMessage error={captchaErrorMessage} />
                    </Col>
                  </Row>
                  }

                  {!isReadOnly && captchaPublicSiteKey && <Row className="b2b-profile__captcha">
                    <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
                      <HCaptcha
                        ref={captchaRef}
                        sitekey={captchaPublicSiteKey}
                        onVerify={handleCaptchaChange}
                        hl={currentLanguage}
                      />
                    </Col>
                  </Row>
                  }
                  {!isReadOnly && (
                    <Row>
                      <Col
                        tablet={{ cols: 4, offset: 4 }}
                        desktop={{ cols: 4, offset: 6 }}
                      >
                        <Button
                          submit
                          primary
                          continue
                          disabled={formikProps.isSubmitting}
                        >
                          {translate(SUBMIT)}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </form>
              )}
            </Formik>

            <Row>
              <Col desktop={{ cols: 8, offset: 2 }}>
                <hr className="b2b-profile__separator" />
              </Col>
            </Row>
          </>
        )}

        <Row>
          <Col
            tablet={{ cols: 4 }}
            desktop={{ cols: 4, offset: 2 }}
          >
            <ScreenSectionHeading light>
              {translate(LOGIN_CREDENTIALS_HEADING)}
            </ScreenSectionHeading>

            <div className={classNames("b2b-profile__contact-detail", { "b2b-profile__contact-detail--gap": !isReadOnly })}>
              <div className="b2b-profile__login-label">{translate(E_MAIL)}</div>
              <div>{userEmail}</div>
            </div>
          </Col>
        </Row>

        {!isReadOnly && (
          <Formik
            initialValues={initialPasswordValues}
            validate={validatePassword}
            onSubmit={onSubmitPassword}
          >
            {(formikProps) => (
              <form onSubmit={formikProps.handleSubmit}>
                <Row className="b2b-profile__password-fields">
                  <Col tablet={{ cols: 4 }} desktop={{ cols: 4, offset: 2 }} >
                    <FormikLabeledField
                      className="b2b-profile__form-row"
                      name="Customer:currentPassword"
                      as={FormikPasswordInput}
                      isFieldRequired={isPasswordFieldRequired}
                      getFieldLabel={getPasswordFieldLabel}
                    />
                  </Col>
                  <Col tablet={{ cols: 4 }} desktop={{ cols: 4 }}>
                    <FormikLabeledField
                      className="b2b-profile__form-row"
                      name="Customer:password"
                      as={FormikPasswordInput}
                      isFieldRequired={isPasswordFieldRequired}
                      getFieldLabel={getPasswordFieldLabel}
                    />
                  </Col>
                </Row>

                {captchaErrorMessage && <Row>
                  <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
                    <ErrorMessage error={captchaErrorMessage} />
                  </Col>
                </Row>
                }

                {captchaPublicSiteKey && <Row className="b2b-profile__captcha">
                  <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={captchaPublicSiteKey}
                      onVerify={handleCaptchaChange}
                      hl={currentLanguage}
                    />
                  </Col>
                </Row>
                }

                <Row>
                  <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }} >
                    <Button submit primary continue disabled={formikProps.isSubmitting}>
                      {translate(SUBMIT)}
                    </Button>
                  </Col>
                </Row>
              </form>
            )}
          </Formik>
        )}

        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <hr className="b2b-profile__separator" />
          </Col>
        </Row>

        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <ScreenSectionHeading light>
              {translate(LEGAL_HEADING)}
            </ScreenSectionHeading>
          </Col>
        </Row>

        <Formik
          initialValues={initialConsentValues}
          validate={validateConsent}
          onSubmit={onSubmitConsent}
        >
          {(formikProps) => (
            <form onSubmit={formikProps.handleSubmit}>
              <ConsentFieldset
                {...formikProps}
                className="b2b-profile__consent-fieldset"
                getFieldLabel={getConsentFieldLabel}
                isReadOnly={isReadOnly}
              />

              {!isReadOnly && captchaErrorMessage && <Row>
                <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
                  <ErrorMessage error={captchaErrorMessage} />
                </Col>
              </Row>
              }

              {!isReadOnly && captchaPublicSiteKey && <Row className="b2b-profile__captcha">
                <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={captchaPublicSiteKey}
                    onVerify={handleCaptchaChange}
                    hl={currentLanguage}
                  />
                </Col>
              </Row>
              }

              {!isReadOnly && (
                <Row>
                  <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
                    <Button submit primary continue disabled={formikProps.isSubmitting}>
                      {translate(SUBMIT)}
                    </Button>
                  </Col>
                </Row>
              )}
            </form>
          )}
        </Formik>
      </TypeArea>
    </ScreenSection>
  );
}

export default function B2B_Profile() {

  const dispatch = useDispatch();

  const [captchaToken, setCaptchaToken] = useState();

  const onCaptchaChange = useCallback((captchaToken) => {
    setCaptchaToken(captchaToken);
  }, []);

  const onSubmitTaxData = useCallback((formValues, formikProps) => {
    formValues = { ...formValues, captchaToken };
    dispatch(submitTaxData(formValues, formikProps));
  }, [dispatch, captchaToken]);

  const onSubmitConsent = useCallback((formValues, formikProps) => {
    formValues = { ...formValues, captchaToken };
    dispatch(submitConsent(formValues, formikProps));

  }, [dispatch, captchaToken]);

  const onSubmitPassword = useCallback((formValues, formikProps) => {
    formValues = { ...formValues, captchaToken };
    dispatch(submitPassword(formValues, formikProps));
  }, [dispatch, captchaToken]);



  const init = useCallback(async () => await dispatch(initialize()), [dispatch]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  } else {
    return (
      <InternalProfileForm
        onSubmitTaxData={onSubmitTaxData}
        onSubmitConsent={onSubmitConsent}
        onSubmitPassword={onSubmitPassword}
        onCaptchaChange={onCaptchaChange}
      />
    );
  }
}
